import { Badge, Icon, IconButton, Image } from "components";
import { copyText, isEmail, share, sortHashtags } from "helpers";
import { useClickAndView } from "hooks";
import { useMemo } from "react";
import toast from "react-hot-toast";
import { Link } from "types";

type Props = {
  link: Link.Item;
};

export default function LinkCard({ link }: Props) {
  const [clickAndView] = useClickAndView({
    contentId: link.id,
    variant: "link",
  });
  const url = useMemo(() => {
    const isHttps = link.urlKey.startsWith("https://");
    if (isEmail(link.urlKey)) return `mailto:${link.urlKey}`;
    if (isHttps) return link.urlKey;
    return `https://${link.urlKey}`;
  }, [link.urlKey]);
  const urlKey = useMemo(() => {
    return url
      .replace("https://", "")
      .replace("http://", "")
      .replace("mailto:", "");
  }, [url]);

  const handleClick = () => {
    clickAndView();
    share({ title: link.title, text: link.description, url }).catch(() => {
      copyText(url)
        .then(() => {
          const message = "Link copied successfully";
          toast.success(message);
        })
        .catch(() => {
          const message = "Error";
          toast.error(message);
        });
    });
  };
  return (
    <article className="w-full">
      <a href={url} target="_blank" rel="noreferrer noopener">
        <div className="w-full flex items-center gap-2.5 py-2.5">
          <Image
            src={link.linkTypeInfo?.iconUrl}
            alt={link.title}
            className="block size-12 rounded min-w-0"
          />
          <div className="flex-1 min-w-0 space-y-1">
            <h3 className="w-full text-base text-[#353740] font-medium break-words">
              {link.title}
            </h3>
            <p className="w-full text-sm text-[#5E6278] font-medium truncate">
              {urlKey}
            </p>
          </div>
        </div>
        <p className="w-full text-sm text-[#5E6278] whitespace-pre-wrap font-normal p-1">
          {link.description}
        </p>
        <div className="flex flex-wrap items-center gap-1 pt-1 pb-2.5">
          {sortHashtags(link.hashtag)?.map((e) => (
            <Badge key={e}>#{e}</Badge>
          ))}
        </div>
      </a>
      <IconButton
        type="button"
        variant="outline"
        className="flex rounded py-1 px-6 text-[#5E6278] border-[#ECEFF2] ms-auto"
        onClick={handleClick}
      >
        <Icon name="ExportCurve" variant="Linear" />
      </IconButton>
    </article>
  );
}
